import CloseIcon from "@/assets/CloseIcon";
import { GraphyVsThinkific } from "@/assets/graphy-vs-thinkic";
import { GraphyVsThinkificMweb } from "@/assets/graphy-vs-thinkicMweb";
import { ButtonSizeEnum, ButtonTypeEnum } from '@/components/atoms/Button';
import CtaButtonGroup from '@/components/molecules/CtaButtonGroup';
import { EXIT_INTENT_EVENTS } from "@/constants/amplitude.constants";
import { UCE_CALENDLY_LINK } from "@/shared/constants/calendly.constants";
import Amplitude from "@/shared/services/amplitude.service";
import { getCurrentURL } from "@/shared/utils/commons";
import { handleFreshsalesSubmission } from "@/shared/utils/freshsales.util";
import { getUTMFieldsFromLocalStorage } from '@/shared/utils/utm.util';
import { CountryPhoneCodeInput, INPUT_TYPES, Input } from "@graphy/ui";
import type { CountryCode } from 'libphonenumber-js/types';
import React, { useCallback, useRef, useState } from 'react';
import { isMobile } from "react-device-detect";
import { getCountryCallingCode } from "react-phone-number-input";
import { toast } from "react-toastify";

interface ExitIntentFormProps {
  onSuccess: () => void;
  onClose: () => void;
  showModal: boolean;
}

const ExitIntentForm: React.FC<ExitIntentFormProps> = ({ onSuccess, onClose, showModal }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: ''
  });

  const [errors, setErrors] = useState({
    fullName: '',
    email: '',
    submission: ''
  });

  const [countryCode, setCountryCode] = useState<CountryCode>("US");
  const [callingCode, setCallingCode] = useState("");
  const contactNumberInputRef = useRef<HTMLInputElement>(null);

  const handleBlur = (field: keyof typeof formData) => (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const variant = sessionStorage.getItem('exitIntentVariant');
    const amplitude = new Amplitude();
    const utmParams = getUTMFieldsFromLocalStorage();

    switch(field) {
      case 'fullName':
        if (value.trim()) {
          amplitude.sendEvent({
            type: EXIT_INTENT_EVENTS.POPUP_NAME_ENTERED.LABEL,
            properties: {
              current_url: getCurrentURL(),
              ...utmParams,
              variant
            }
          });
        }
        break;
      case 'email':
        if (/\S+@\S+\.\S+/.test(value)) {
          amplitude.sendEvent({
            type: EXIT_INTENT_EVENTS.POPUP_EMAIL_ENTERED.LABEL,
            properties: {
              current_url: getCurrentURL(),
              ...utmParams,
              variant
            }
          });
        }
        break;
      case 'phone':
        if (value.trim().length >= 10) {
          amplitude.sendEvent({
            type: EXIT_INTENT_EVENTS.POPUP_PHONE_ENTERED.LABEL,
            properties: {
              current_url: getCurrentURL(),
              ...utmParams,
              variant
            }
          });
        }
        break;
    }
  };

  const handleChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setErrors({ fullName: '', email: '', submission: '' });

    if (!formData.fullName) {
      setErrors(prev => ({ ...prev, fullName: 'Full name is required' }));
      return;
    }
    if (!formData.email) {
      setErrors(prev => ({ ...prev, email: 'Email is required' }));
      return;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email' }));
      return;
    }

    try {
        const amplitude = new Amplitude();
        const utmParams = getUTMFieldsFromLocalStorage();
        amplitude.sendEvent({
            type: EXIT_INTENT_EVENTS.POPUP_JOIN_NOW.LABEL,
            properties: {
                current_url: getCurrentURL(),
                ...utmParams,
                variant: sessionStorage.getItem('exitIntentVariant'),
                name: formData.fullName,
                email: formData.email,
                phone: formData.phone
            }
        });

        const submitted = await handleFreshsalesSubmission(formData);
        
        if (submitted) {
            sessionStorage.setItem('exitIntentFormSubmitted', 'true');
            sessionStorage.setItem('BOOK_DEMO_TAPPED', 'true');
            sessionStorage.setItem('hasClickedDemo', 'true');

            const utmFields = getUTMFieldsFromLocalStorage();
            const utmParamsString = Object.entries(utmFields)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
                .join('&');

            const calendlyUrl = `${UCE_CALENDLY_LINK}?name=${encodeURIComponent(formData.fullName)}&email=${encodeURIComponent(formData.email)}&${utmParamsString}`;

            window.open(calendlyUrl, '_blank');
            onSuccess();
            toast.success("Submitted successfully.");
        } else {
          
            setErrors(prev => ({ ...prev, submission: 'Failed to submit form. Please try again.' }));
        }
    } catch (error) {
        console.error('Error:', error);
        setErrors(prev => ({ ...prev, submission: 'Failed to submit form. Please try again.' }));
    }
};

  const handleClose = () => {
    const amplitude = new Amplitude();
    const utmParams = getUTMFieldsFromLocalStorage();
    
    amplitude.sendEvent({
      type: EXIT_INTENT_EVENTS.POPUP_DISMISSED.LABEL,
      properties: {
        current_url: getCurrentURL(),
        ...utmParams,
        variant: sessionStorage.getItem('exitIntentVariant'),
        email: formData.email
      }
    });
    onClose();
  };

  const onCountryChange = useCallback((value: any) => {
    const callingCode = getCountryCallingCode(value.code as CountryCode);
    setCountryCode(value.code as CountryCode);
    setCallingCode(callingCode);
    contactNumberInputRef.current?.focus();
  }, []);

  if (!showModal) return null;

  const renderForm = () => {
    const formContent = (
      <>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <input
              type="text"
              placeholder="Full Name"
              required
              className={`h-[40px] px-4 py-3 border border-[#E9E9F2] rounded-lg bg-white ${isMobile ? 'w-full' : 'w-[320px]'}`}
              value={formData.fullName}
              onChange={handleChange('fullName')}
              onBlur={handleBlur('fullName')}
              onKeyDown={handleKeyPress}
            />
            {errors.fullName && (
              <span className="text-sm text-[#FF000080] mt-1">
                Please enter name
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <input
              type="email"
              placeholder="Email"
              required
              className={`h-[40px] px-4 py-3 border border-[#E9E9F2] rounded-lg bg-white ${isMobile ? 'w-full' : 'w-[320px]'}`}
              value={formData.email}
              onChange={handleChange('email')}
              onBlur={handleBlur('email')}
              onKeyDown={handleKeyPress}
            />
            {errors.email && (
              <span className="text-sm text-[#FF000080] mt-1">
                Email is invalid
              </span>
            )}
          </div>

          <div id="phone-input-wrapper" className="flex items-center relative sm:w-[329px] md:w-[322px] lg:w-[322px]">
            <div className="rounded-lg bg-white relative flex items-center">
              <CountryPhoneCodeInput
                key={countryCode}
                countryCode={countryCode}
                phoneInputClassName="h-[44px] flex-1 text-grey-3 bg-white"
                onSelected={onCountryChange}
                label=""
                isShortLabel={true}
                borderLight={true}
              />
              {/* Add custom arrow */}
              <div className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer">
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L5 5L9 1"
                    stroke="#666666"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <Input
              ref={contactNumberInputRef}
              type={INPUT_TYPES.NUMBER}
              containerMargin="ml-4"
              inputPadding="p-[0.65rem]"
              className="flex-2 h-12 w-full rounded-[8px] border border-[#E9E9F2] p-[16px] text-body text-darkSlate01"
              value={formData.phone}
              placeholder="Phone (optional)"
              onChange={(value) => handleChange('phone')({ target: { value } } as any)}
              // onBlur={(e) => handleBlur('phone')(e as any)}
            />
          </div>

          {errors.submission && (
            <span className="text-sm !text-red-500 text-center mt-1 font-medium">
              {errors.submission}
            </span>
          )}

          <div className={`${isMobile ? 'w-full' : 'w-[321px]'} h-[40px]`}>
            <CtaButtonGroup
              ctas={[{
                type: ButtonTypeEnum.Primary,
                size: ButtonSizeEnum.Regular,
                text: "Join Now",
                onClick: handleSubmit,
                utmFields: {
                  ...getUTMFieldsFromLocalStorage(),
                  utm_source: "exit_intent",
                  utm_medium: "popup",
                  utm_campaign: "thinkific_alternative"
                },
                amplitudeEvent: {
                  eventName: "Exit Intent Demo Requested",
                  eventProperties: {
                    source: "exit_intent_popup",
                    competitor: "thinkific",
                    form_data: formData
                  }
                }
              }]}
              fullWidth={true}
            />
          </div>
        </div>
      </>
    );

    return formContent;
  };

  return (
    <div className="fixed inset-0 z-modalOverlay flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto">
      {isMobile ? (
        <div className="absolute bottom-0 left-0 right-0 flex flex-col rounded-t-[24px] bg-white h-[575.42px]">
          {/* Cross button section */}
          <div className="w-[362px] mx-auto h-[56px] pt-4 flex items-start px-4">
            <CloseIcon onClick={handleClose} />
          </div>

          {/* Thinkific logo/comparison section */}
          <div className="w-[330px] mx-auto h-[131.42px] rounded-tl-[12.61px]">
            <GraphyVsThinkificMweb />
          </div>

          {/* Text header and description */}
          <div className="w-[362px] mx-auto h-[152px] pt-4 px-4 flex flex-col gap-4">
            <h2 className="h-[52px] text-[24px] font-semibold leading-[32px] tracking-[-0.005em] text-[#3A3D50]">
              See how Graphy compares to Thinkific
            </h2>
            <p className="text-[#3A3D50] h-[60px]">
              Join 2000+ creators who switched from Thinkific to Graphy to create courses,
              sell digital products, build community, and much more.
            </p>
          </div>

          {/* Form section */}
          <div className="w-[362px] mx-auto h-[232px] px-4 pb-6 flex flex-col gap-16 pt-[2px]">
            {renderForm()}
          </div>
        </div>
      ) : (
        <div className="w-[712px] mt-[120px] bg-white rounded-lg relative max-w-[95%] mx-auto">
          <div className="flex flex-col md:flex-row p-[24px]">
            <div className="w-full md:w-[320px] h-[432px] rounded-tl-lg overflow-hidden">
              <GraphyVsThinkific />
            </div>

            <div className="w-full md:w-[320px] h-[432px] pt-[10px]">
              <div className="flex flex-col pl-5">
                <div className="absolute top-3 right-5">
                  <CloseIcon onClick={handleClose} />
                </div>
                <div className="text-center">
                  <h2 className="text-[24px] font-semibold leading-[32px] tracking-[-0.005em] text-left w-[320px] h-[64px] text-[#3A3D50]">
                    See how Graphy compares to Thinkific
                  </h2>
                  <p className="text-[#3A3D50] text-left pt-2 font-normal">
                    Join 2000+ creators who switched from Thinkific to Graphy to create courses,
                    sell digital products, build community, and much more.
                  </p>
                </div>
                <div className="pt-[26px]">
                  {renderForm()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExitIntentForm;